import React, { useState, useEffect } from 'react';
import { 
    getSiteVersions, 
    rollbackToVersion, 
    getSiteInfo,
    firestorePublisherUtils 
} from '../utils/firestorePublisher';
import './VersionManager.css';

/**
 * 🔄 Version Manager Component
 * 
 * Verwaltet Firestore Site-Versionen mit Rollback-Funktionalität
 */
const VersionManager = ({ siteId, isOpen, onClose, onRollback }) => {
    const [versions, setVersions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [siteInfo, setSiteInfo] = useState(null);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [isRollingBack, setIsRollingBack] = useState(false);

    useEffect(() => {
        if (isOpen && siteId) {
            loadVersions();
        }
    }, [isOpen, siteId]);

    const loadVersions = async () => {
        setLoading(true);
        try {
            const [versionsData, siteData] = await Promise.all([
                getSiteVersions(siteId, 20), // Load last 20 versions
                getSiteInfo(siteId)
            ]);
            
            setVersions(versionsData);
            setSiteInfo(siteData);
            console.log('📦 Versions loaded:', versionsData);
        } catch (error) {
            console.error('❌ Error loading versions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleRollback = async (versionId) => {
        if (!selectedVersion || selectedVersion.id === siteInfo?.publishedVersion) {
            return;
        }

        const confirmRollback = window.confirm(
            `Are you sure you want to rollback to version ${selectedVersion.versionId}?\n\n` +
            `This will make version ${selectedVersion.versionId} the new live version.`
        );

        if (!confirmRollback) return;

        setIsRollingBack(true);
        try {
            const rollbackNote = `Rollback to ${selectedVersion.versionId} - ${selectedVersion.note || 'No note'}`;
            const result = await rollbackToVersion(siteId, versionId, rollbackNote);
            
            console.log('✅ Rollback successful:', result);
            
            // Reload versions and notify parent
            await loadVersions();
            onRollback?.(result);
            
            // Show success message
            alert(`Successfully rolled back to version ${selectedVersion.versionId}`);
            
        } catch (error) {
            console.error('❌ Rollback failed:', error);
            alert('Rollback failed. Please try again.');
        } finally {
            setIsRollingBack(false);
            setSelectedVersion(null);
        }
    };

    const formatDate = (date) => {
        if (!date) return 'Unknown';
        const d = date instanceof Date ? date : new Date(date);
        return d.toLocaleString();
    };

    const getVersionStatus = (version) => {
        if (version.versionId === siteInfo?.publishedVersion) {
            return 'current';
        }
        return 'archived';
    };

    if (!isOpen) return null;

    return (
        <div className="version-manager-overlay">
            <div className="version-manager-modal">
                <div className="version-manager-header">
                    <h3>
                        <i className="fas fa-history"></i>
                        Version History
                    </h3>
                    <button 
                        className="close-btn"
                        onClick={onClose}
                        title="Close"
                    >
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="version-manager-content">
                    {loading ? (
                        <div className="loading-state">
                            <i className="fas fa-spinner fa-spin"></i>
                            Loading versions...
                        </div>
                    ) : versions.length === 0 ? (
                        <div className="empty-state">
                            <i className="fas fa-archive"></i>
                            <p>No versions found</p>
                            <small>Publish your page to create the first version</small>
                        </div>
                    ) : (
                        <>
                            <div className="site-info">
                                <div className="site-info-item">
                                    <strong>Site:</strong> {siteInfo?.name || 'Unknown'}
                                </div>
                                <div className="site-info-item">
                                    <strong>Current Version:</strong> 
                                    <span className="current-version">
                                        {siteInfo?.publishedVersion || 'None'}
                                    </span>
                                </div>
                                <div className="site-info-item">
                                    <strong>Total Versions:</strong> {versions.length}
                                </div>
                            </div>

                            <div className="versions-list">
                                {versions.map((version) => (
                                    <div 
                                        key={version.id}
                                        className={`version-item ${getVersionStatus(version)} ${selectedVersion?.id === version.id ? 'selected' : ''}`}
                                        onClick={() => setSelectedVersion(version)}
                                    >
                                        <div className="version-header">
                                            <div className="version-id">
                                                <i className={`fas ${getVersionStatus(version) === 'current' ? 'fa-star' : 'fa-archive'}`}></i>
                                                {version.versionId}
                                                {getVersionStatus(version) === 'current' && (
                                                    <span className="current-badge">LIVE</span>
                                                )}
                                            </div>
                                            <div className="version-date">
                                                {formatDate(version.publishedAt)}
                                            </div>
                                        </div>

                                        <div className="version-details">
                                            <div className="version-note">
                                                {version.note || 'No description'}
                                            </div>
                                            <div className="version-meta">
                                                <span className="version-author">
                                                    <i className="fas fa-user"></i>
                                                    {version.publishedBy || 'Unknown'}
                                                </span>
                                                <span className="version-status">
                                                    <i className="fas fa-circle" style={{ 
                                                        color: version.status === 'live' ? '#4CAF50' : '#999' 
                                                    }}></i>
                                                    {version.status || 'archived'}
                                                </span>
                                            </div>
                                        </div>

                                        {selectedVersion?.id === version.id && (
                                            <div className="version-actions">
                                                {getVersionStatus(version) !== 'current' && (
                                                    <button
                                                        className="rollback-btn"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleRollback(version.versionId);
                                                        }}
                                                        disabled={isRollingBack}
                                                    >
                                                        {isRollingBack ? (
                                                            <>
                                                                <i className="fas fa-spinner fa-spin"></i>
                                                                Rolling back...
                                                            </>
                                                        ) : (
                                                            <>
                                                                <i className="fas fa-undo"></i>
                                                                Rollback to this version
                                                            </>
                                                        )}
                                                    </button>
                                                )}
                                                <button
                                                    className="preview-btn"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        // TODO: Implement preview functionality
                                                        alert('Preview functionality coming soon!');
                                                    }}
                                                >
                                                    <i className="fas fa-eye"></i>
                                                    Preview
                                                </button>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                <div className="version-manager-footer">
                    <div className="footer-info">
                        <i className="fas fa-info-circle"></i>
                        Click on a version to see rollback options
                    </div>
                    <button 
                        className="refresh-btn"
                        onClick={loadVersions}
                        disabled={loading}
                    >
                        <i className="fas fa-sync-alt"></i>
                        Refresh
                    </button>
                </div>
            </div>
        </div>
    );
};

export default VersionManager;
